import React, { FC, ReactElement, useLayoutEffect, useRef } from 'react';

import classNames from 'classnames';

import { ISubHeaderProps } from './sub-header.interface';
import styles from './sub-header.module.scss';

import { Container } from 'views/components/UI/Container';

const SUB_HEADER_HEIGHT_VAR = '--sub-header-height';

const SubHeader: FC<ISubHeaderProps> = ({
  children,
  className,
  noPaddingBottom,
  position = 'fixed',
}): ReactElement => {
  const subHeaderRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    if (position === 'static') {
      document.documentElement.style.setProperty(SUB_HEADER_HEIGHT_VAR, '0px');
      return undefined;
    }
    const subHeaderEl = subHeaderRef.current;
    if (!subHeaderEl) {
      return undefined;
    }

    // Deferred a frame so the style mutation below never lands inside the
    // same synchronous notification cycle that triggered it — writing a CSS
    // custom property from a ResizeObserver callback can itself cause a
    // reflow the observer then reacts to again, which is what the browser's
    // "ResizeObserver loop completed with undelivered notifications" error
    // is warning about.
    let rafId: number | null = null;

    const updateHeight = (): void => {
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }

      rafId = requestAnimationFrame(() => {
        document.documentElement.style.setProperty(
          SUB_HEADER_HEIGHT_VAR,
          `${subHeaderEl.offsetHeight}px`,
        );
      });
    };

    updateHeight();

    if (!window.ResizeObserver) {
      window.addEventListener('resize', updateHeight);
      return () => {
        window.removeEventListener('resize', updateHeight);
        if (rafId !== null) {
          cancelAnimationFrame(rafId);
        }
        document.documentElement.style.setProperty(SUB_HEADER_HEIGHT_VAR, '0px');
      };
    }

    const resizeObserver = new ResizeObserver(updateHeight);
    resizeObserver.observe(subHeaderEl);

    return () => {
      resizeObserver.disconnect();
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      document.documentElement.style.setProperty(SUB_HEADER_HEIGHT_VAR, '0px');
    };
  }, [position]);

  return (
    <div
      ref={subHeaderRef}
      className={classNames(styles['sub-header'], className, {
        [styles['sub-header--no-padding-bottom']]: noPaddingBottom,
        [styles['sub-header--static']]: position === 'static',
      })}
    >
      <Container className={styles['sub-header__container']}>{children}</Container>
    </div>
  );
};

export { SubHeader };
